import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDateTime, formatNumber } from '@/lib/format'
import { emailDomain } from '@/lib/email'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Pagination } from '@/components/Pagination'
import { EmptyState } from '@/components/EmptyState'
import { ContactCompany } from '@/components/CompanyLogo'
import { ArrowLeft, Layers, Search, Users } from 'lucide-react'
import { useSegment, useContacts } from '@/hooks/useApi'
import { useDebounce } from '@/hooks/useDebounce'

export function SegmentDetail() {
  const { t } = useTranslation('contacts')
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 300)

  const { data: segment, isLoading: segmentLoading, isError } = useSegment(id)
  const { data: contacts, isLoading: contactsLoading } = useContacts(page, 50, {
    segmentId: id,
    search: debouncedSearch,
  })

  const formatDate = (iso: string | null) => {
    if (!iso) return t('segments.neverSynced')
    return formatDateTime(iso, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const backLink = (
    <Link
      to="/segments"
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
    >
      <ArrowLeft className="h-4 w-4" /> {t('segments.detail.back')}
    </Link>
  )

  if (isError) {
    return (
      <div>
        {backLink}
        <EmptyState
          icon={Layers}
          title={t('segments.detail.notFound.title')}
          description={t('segments.detail.notFound.description')}
        />
      </div>
    )
  }

  return (
    <div>
      {backLink}

      <div className="mb-8">
        {segmentLoading || !segment ? (
          <Skeleton className="h-9 w-64" />
        ) : (
          <>
            <h1 className="text-3xl font-bold">{segment.name}</h1>
            <p className="text-muted-foreground mt-1">
              {t('segments.detail.synced', { count: formatNumber(segment.contact_count) })} ·{' '}
              {t('segments.detail.hubspotSize', { count: formatNumber(segment.hubspot_size) })} ·{' '}
              {t('segments.detail.lastSyncLabel', { date: formatDate(segment.last_synced_at) })}
            </p>
          </>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle>{t('segments.detail.contactsTitle')}</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('segments.detail.searchPlaceholder')}
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value)
                  setPage(1)
                }}
                className="pl-9 w-72"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {contactsLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : contacts && contacts.items.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('table.name')}</TableHead>
                    <TableHead>{t('table.email')}</TableHead>
                    <TableHead>{t('table.qualifica')}</TableHead>
                    <TableHead>{t('table.company')}</TableHead>
                    <TableHead>{t('table.emailDomain')}</TableHead>
                    <TableHead>{t('table.owner')}</TableHead>
                    <TableHead>{t('table.saluto')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.items.map((c) => (
                    <TableRow
                      key={c.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/contacts/${c.id}`)}
                    >
                      <TableCell className="font-medium">{c.full_name}</TableCell>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>{c.properties.jobtitle || '-'}</TableCell>
                      <TableCell><ContactCompany contact={c} /></TableCell>
                      <TableCell>{emailDomain(c.email) || '-'}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {c.hubspot_owner_email || '-'}
                      </TableCell>
                      <TableCell>{c.properties.saluto || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                page={page}
                totalPages={contacts.pages}
                onPageChange={setPage}
              />
            </>
          ) : (
            <EmptyState
              icon={Users}
              title={
                debouncedSearch
                  ? t('segments.detail.searchEmpty.title')
                  : t('segments.detail.empty.title')
              }
              description={
                debouncedSearch
                  ? t('segments.detail.searchEmpty.description')
                  : t('segments.detail.empty.description')
              }
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
